import { useLocalStorage } from '@vueuse/core'

import type { AssessmentQuestion, QuizAnswer } from '@ts-assessment/interfaces'

import { useAssessmentTestStore } from '@stores/assessment-test'
import { useDisciplineStore } from '@stores/discipline'
import { useCustomerStore } from '@stores/me/customer'

import { useAssessmentCreateApi } from '@api/assessments/assessments-create.hook'
import { useKidRoutes } from '@composables/routes/kid-route.hook'
import { useToast } from '@hooks/toast/index.hook'

import amplitudeConfig from '@config/amplitude.json'

import { trackEvent } from '@tools/amplitude'
import { getNotifyChannelFromUrl } from '@tools/amplitude/notify-channel'
import { getNotifyTypeFromUrl } from '@tools/amplitude/notify-type'

interface AssessmentTestControlReturn {
  setSkipStep: () => void
  setPrevStep: () => void
  setNextStep: () => void
  setSubmitAnswers: () => void
  setSubmitCompiling: () => Promise<void>
  setAssessmentTestAnswer: (answer: QuizAnswer | null) => void
}

export const useAssessmentTestControl = (): AssessmentTestControlReturn => {
  const customerStore = useCustomerStore()
  const assessmentStore = useAssessmentTestStore()
  const disciplineStore = useDisciplineStore()
  const toast = useToast()

  const {
    isRouteDisciplineMath,
    getAssessmentMathResultUrl,
    getAssessmentElaResultUrl,
    getAssessmentCatchUpMathResultUrl,
    getAssessmentCatchUpElaResultUrl,
  } = useKidRoutes()

  const answersKey = computed(() => `assessment-test-answers-${disciplineStore.activeDiscipline?.id}`)
  const stepKey = computed(() => `assessment-quiz-step-${disciplineStore.activeDiscipline?.id}`)

  const answers = useLocalStorage<string>(answersKey.value, null)
  const step = useLocalStorage<string | null>(stepKey.value, null)

  const questionIndex = computed(() => assessmentStore.getQuestions.findIndex(question => question.name === assessmentStore.getCurrentStep))

  const prevStep = computed(() => {
    return questionIndex.value > 0
      ? assessmentStore.getQuestions[questionIndex.value - 1]?.name
      : assessmentStore.getHomeStep
  })

  const nextStep = computed(() => {
    return questionIndex.value < assessmentStore.getQuestions.length - 1
      ? assessmentStore.getQuestions[questionIndex.value + 1]?.name
      : assessmentStore.getHomeStep
  })

  const getResults = (): AssessmentQuestion[] => {
    return assessmentStore.getQuestions.map(question => {
      const answers = question.answers.map(answer => {
        const selected = assessmentStore.getAnswers?.[question.name]?.answerName === answer.name
        return {
          ...answer,
          selected,
        }
      })

      return {
        ...question,
        answers,
      }
    })
  }

  const getScore = (results: AssessmentQuestion[]): number => {
    return results.reduce((acc, question) => {
      return acc + question.answers.reduce((innerAcc, answer) => {
        return innerAcc + (answer.correct && answer.selected ? 1 : 0)
      }, 0)
    }, 0)
  }

  const setAssessmentTestAnswer = (answer: QuizAnswer | null): void => {
    const questionName = assessmentStore.getCurrentQuestion?.name
    if (!answer || !questionName) return
    assessmentStore.setAnswer(answer)
    const body = {
      ...JSON.parse(answers.value),
      [questionName]: answer,
    }
    answers.value = JSON.stringify(body)
  }

  const setAssessmentTestStep = (stepName?: string | null): void => {
    const question = assessmentStore.getQuestions.find(question => question.name === stepName)
    if (stepName) {
      const storedAnswer = assessmentStore.getAnswers?.[stepName]
      assessmentStore.setCurrentAnswer(storedAnswer || null)
    }
    assessmentStore.setCurrentStep(stepName || null)
    assessmentStore.setCurrentQuestion(question || null)
    step.value = stepName ?? null
  }

  const setNextStep = (): void => {
    setAssessmentTestAnswer(assessmentStore.getCurrentAnswer)
    setAssessmentTestStep(nextStep.value ?? null)

    trackEvent(amplitudeConfig.events.clickSubmitQuestion, {
      discipline_id: disciplineStore.getDisciplineInfo?.id,
      discipline_code: disciplineStore.getDisciplineCode,
      grade: disciplineStore.getKidGrade,
      notify_type: getNotifyTypeFromUrl(),
      notify_channel: getNotifyChannelFromUrl(),
      ...assessmentStore.getCurrentAnswer,
    })
  }

  const setPrevStep = (): void => {
    setAssessmentTestStep(prevStep.value ?? null)
  }

  const setSkipStep = (): void => {
    trackEvent(amplitudeConfig.events.clickSkipQuestion, {
      discipline_id: disciplineStore.getDisciplineInfo?.id,
      discipline_code: disciplineStore.getDisciplineCode,
      grade: disciplineStore.getKidGrade,
      notify_type: getNotifyTypeFromUrl(),
      notify_channel: getNotifyChannelFromUrl(),
      question_name: assessmentStore.getCurrentQuestion?.name,
      question_title: assessmentStore.getCurrentQuestion?.title,
    })

    setAssessmentTestStep(nextStep.value ?? null)
  }

  const setSubmitAnswers = (): void => {
    setAssessmentTestAnswer(assessmentStore.getCurrentAnswer)
    setAssessmentTestStep()

    trackEvent(amplitudeConfig.events.clickSubmitQuestion, {
      discipline_id: disciplineStore.getDisciplineInfo?.id,
      discipline_code: disciplineStore.getDisciplineCode,
      grade: disciplineStore.getKidGrade,
      notify_type: getNotifyTypeFromUrl(),
      notify_channel: getNotifyChannelFromUrl(),
      ...assessmentStore.getCurrentAnswer,
    })

    assessmentStore.setCompiling(true)
  }

  const setSubmitCompiling = async (): Promise<void> => {
    const results = getResults()
    const score = getScore(results)
    const comprehension = assessmentStore.isComprehension

    await useAssessmentCreateApi({
      results,
      score,
      comprehension,
    }, {
      onSuccess: async (id) => {
        const urlAssessment = isRouteDisciplineMath.value ?  getAssessmentMathResultUrl(id) : getAssessmentElaResultUrl(id)
        const urlComprehension = isRouteDisciplineMath.value ?  getAssessmentCatchUpMathResultUrl(id) : getAssessmentCatchUpElaResultUrl(id)
        const url = comprehension ? urlComprehension : urlAssessment
        await customerStore.ensure({ force: true })
        navigateTo(url)
      },
      onError: () => toast.error({
        title: 'Error!',
        description: 'Invalid result link URL.',
      }),
    })
  }

  return {
    setNextStep,
    setPrevStep,
    setSkipStep,
    setSubmitAnswers,
    setSubmitCompiling,
    setAssessmentTestAnswer,
  }
}
